import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import axiosInstance from "../../Context/axiosInstance";
import { DarkModeContext } from "../../Context/DarkModeContext";
import { GeoContext } from "../../Context/GeoContext";

export default function FoldersPage() {
  const navigate = useNavigate();
  const { isDarkMode } = useContext(DarkModeContext);

  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  const { store, loadingGeo } = useContext(GeoContext);

  useEffect(() => {
    if (!store || loadingGeo) return;

    const fetchFolders = async () => {
      try {
        setLoading(true);

        const { data } = await axiosInstance.get("/folders", {
          params: {
            page,
            pageSize,
            storeId: store.id,
            search: search.trim() || undefined,
          },
        });

        if (data?.response && Array.isArray(data.response)) {
          setFolders(data.response);
          setTotalPages(Math.ceil((data.totalCount || 1) / pageSize));
        } else {
          setFolders([]);
        }
      } catch (error) {
        setFolders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFolders();
  }, [store, loadingGeo, page, pageSize, search]);

  const filteredFolders = folders.filter((folder) =>
    folder.name?.toLowerCase().includes(search.toLowerCase()),
  );

  const handleFolderClick = (folder) => {
    console.log("Clicked folder:", folder); // Check what folder data looks like
    const folderNameSlug = folder.name

      ?.toLowerCase()
      .replace(/\s+/g, "-") // spaces → hyphens
      .replace(/[^\p{L}\p{N}-]+/gu, ""); // keep letters (all scripts), numbers, hyphens

    navigate(`/student-folders/${folderNameSlug}`, {
      state: { folder },
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  return (
    <div
      className={`flex flex-col min-h-screen ${
        isDarkMode ? "bg-[#181818] text-white" : "bg-customBlue text-black"
      }`}
    >
      <main className="min-w-0 flex-1 px-4 pt-8 sm:px-8">
        <div
          className={`relative animate-fadeIn box-border px-5 pt-16 sm:px-8 rounded-lg shadow-lg ${
            isDarkMode ? "bg-[#1e1e1e] text-white" : "bg-white text-black"
          }`}
        >
          {/* Header + Search */}
          <div
            className={`${
              isDarkMode ? "text-white" : "text-gray-700"
            } flex w-full flex-col gap-4 mb-6 text-sm capitalize xl:flex-row xl:items-center xl:justify-between`}
          >
            <h2 className="text-xl font-semibold">📂 Folders</h2>

            <div className="w-full min-w-0 xl:max-w-[680px]">
              <input
                type="text"
                placeholder="Search folders..."
                aria-label="Search folders"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`${
                  isDarkMode
                    ? "bg-[#292929] border border-gray-500 text-white"
                    : "bg-gray-100 border border-gray-300 text-black"
                } px-4 py-2 rounded-lg w-full min-w-0 max-w-[680px]`}
              />
            </div>
          </div>

          {/* Folder Grid */}
          <div
            className={`custom-scrollbar relative rounded-lg ${
              isDarkMode ? "bg-[#1e1e1e] text-white" : "bg-white text-black"
            }`}
            style={{ height: "clamp(320px, 65dvh, 643px)", overflowY: loading ? "hidden" : "auto" }}
          >
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <ClipLoader color="#2264E5" size={50} />
              </div>
            ) : filteredFolders.length > 0 ? (
              <div className="animate-zoomIn mx-auto grid w-full max-w-[252px] grid-cols-1 items-start gap-6 p-4 md:max-w-[740px] md:grid-cols-3 lg:max-w-[1528px] lg:grid-cols-5">
                {filteredFolders.map((folder) => (
                  <div
                    key={folder.id}
                    onClick={() => handleFolderClick(folder)}
                    className={`p-6 rounded-2xl shadow-md text-white cursor-pointer transform hover:scale-105 transition duration-300 ${isDarkMode ? "bg-gradient-to-br from-gray-800 to-gray-700 hover:shadow-gray-600/40" : "bg-gradient-to-br from-blue-400 to-blue-600 hover:shadow-blue-400/40"}`}
                  >
                    <div className="text-4xl mb-4">📂</div>
                    <h2 className="text-lg font-bold truncate">
                      {folder.name || "Untitled Folder"}
                    </h2>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No folders found.
              </div>
            )}
          </div>

          {/* Page Size Control */}
          <div className="flex flex-wrap gap-3 items-center justify-end py-4 text-black">
            <label
              htmlFor="pageSize"
              className={`${
                isDarkMode ? "text-white" : "text-gray-700"
              } mr-2 text-sm`}
            >
              Folders per page:
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={handlePageSizeChange}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            >
              {[20, 30, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          {/* Pagination Controls */}
          <div className="flex justify-center py-4">
            <div>
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`px-4 py-2 ${
                    page === index + 1
                      ? `${
                          isDarkMode ? "bg-customRed" : "bg-customBlue"
                        } text-white`
                      : `${
                          isDarkMode ? "bg-gray-300 text-black" : "bg-gray-300"
                        }`
                  } rounded-[5px] mx-1 my-1`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
